import { config } from '../../../../config';
import DigiobcanApi from '../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { type GpsUsageState } from '../../../components/FilesList';
import MissingInternetConnectionIndicator from '../../../components/MissingInternetConnectionIndicator';
import MissingLocationIndicator from '../../../components/MissingLocationIndicator';
import ReportCreateResultModal from '../../../components/ReportCreateResultModal';
import FormAutocompleteField from '../../../components/form/FormAutocompleteField';
import FormMapField, {
  type FormMapFieldRef,
} from '../../../components/form/FormMapField';
import FormMediaField from '../../../components/form/FormMediaField';
import FormTextField from '../../../components/form/FormTextField';
import { useCityBoundary } from '../../../features/queries/getCityBoundary';
import { getReportCategories } from '../../../features/queries/getReportCategories';
import { meGetReports } from '../../../features/queries/meGetReports';
import { useNetworkMutation } from '../../../hooks/useNetworkOperations';
import { getErrorDisplayInfo } from '../../../lib/NetworkErrorHandler';
import { shortId } from '../../../lib/ids';
import { useInfiniteQ, useQ } from '../../../lib/query';
import { useLayout } from '../../../providers/LayoutProvider';
import { type GeoLocation } from '../../../providers/LocationProvider';
import { useLogger } from '../../../providers/LoggerProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import {
  __,
  composeValidators,
  getLocationMetadataFromMedia,
  required,
  requiredArray,
  toastError,
  validateMaxFileSize,
} from '../../../utils';
import { DEFAULT_PROFILE_SECTION, profilePath } from '../../profil/sections';
import { useAuth, useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Form } from 'react-final-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

/**
 * Oprávnenie zvoliť kategóriu už pri zadaní - dostáva ho rola úradníkov, ktorí
 * zakladajú požiadavky VMČ, požiadavky MsZ a interpelácie poslancov MsZ.
 * Tie majú kategóriu vopred danú a AI by ju len zle hádala. API ho overuje
 * samo (`403 FORBIDDEN_REPORT_CATEGORY_ON_CREATE`); tu len rozhoduje, či sa
 * pole vôbec ukáže.
 */
const CATEGORY_ON_CREATE_PERMISSION = 'pruser:reports:create:category' as const;

type ReportFormData = {
  title: string;
  description: string;
  location: GeoLocation | null;
  media?: File[];
  category_id?: string;
};

const ReportCreatePage: React.FC = () => {
  const { logger } = useLogger();
  const { setActions, setFooter } = useLayout();
  const { isMd, isNotSm, isLg } = useResponsive();
  const { authUser, authState, manualTokenRefresh } = useAuth();
  const { hasPermission } = useAuthRoles();
  const canChooseCategory = React.useMemo(
    () =>
      authState === 'authorized' &&
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: CATEGORY_ON_CREATE_PERMISSION,
      }),
    [authState, hasPermission],
  );

  const { data: categories = [], isLoading: isLoadingCategories } = useQ(
    getReportCategories,
    { view: 'flat' },
    { enabled: canChooseCategory, staleTime: 5 * 60 * 1000 },
  );
  // Cesta od koreňa (`Mestské zastupiteľstvo / Interpelácie`), nie holý názov:
  // pod rôznymi rodičmi môžu stáť kategórie s rovnakým menom.
  const categoryOptions = React.useMemo(
    () =>
      categories
        .map((category) => ({
          value: category.category_id,
          label: category.full_path || category.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sk')),
    [categories],
  );
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { setBreadcrumbs } = useLayout();

  React.useEffect(() => {
    setBreadcrumbs([
      { href: '/', label: __('Domov') },
      ...(authState === 'authorized'
        ? [
            {
              href: profilePath(DEFAULT_PROFILE_SECTION),
              label: __('Moje podnety'),
            },
          ]
        : []),
      { label: 'Nový podnet' },
    ]);
  }, [setBreadcrumbs, authState]);

  const [resultModalState, setResultModalState] = React.useState<{
    open: boolean;
    type: 'success' | 'error';
    message?: string;
    reportId?: string;
  }>({
    open: false,
    type: 'success',
    message: undefined,
    reportId: undefined,
  });
  const [pendingFormData, setPendingFormData] =
    React.useState<ReportFormData | null>(null);

  const mapWrapper = React.useRef<HTMLDivElement>(null);
  const mapFieldRef = React.useRef<FormMapFieldRef>(null);
  const { isInside } = useCityBoundary();

  const { refetch: refetchUserReports } = useInfiniteQ(
    meGetReports,
    {
      limit: 15,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: () => undefined,
    },
  );

  const createReportMutation = useNetworkMutation({
    mutationFn: async (values: {
      title: string;
      description: string;
      media?: File[];
      location?: GeoLocation;
      recaptchaToken?: string;
      category_id?: string;
    }) => {
      if (authState === 'authorized') {
        await manualTokenRefresh();
      }

      const { location, recaptchaToken, ...restValues } = values;
      return DigiobcanApi.createReport(
        {},
        {
          ...restValues,
          ...(location && {
            location: [location[0], location[1], '50'].join(','),
          }),
          recaptchaToken,
        },
      );
    },
    onSuccess: async (report) => {
      if (authUser) {
        await refetchUserReports();
      }

      setResultModalState({
        open: true,
        type: 'success',
        message: __('Ďakujeme za váš podnet'),
        reportId: report.id,
      });
    },
    maxRetries: 3,
    retryDelay: 1000,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any, variables) => {
      logger.error('Error creating report', {
        action: 'createReport',
        error: e,
        data: {
          ...variables,
        },
      });

      let errorMessage = '';

      // Handle API-specific errors first
      if (e.details?.name === 'FORBIDDEN_REPORT_CATEGORY_ON_CREATE') {
        errorMessage = __(
          'Na výber kategórie pri zadaní podnetu nemáte oprávnenie. Odošlite podnet bez kategórie.',
        );
        toastError(errorMessage);
      } else if (e.details?.name === 'REPORT_CATEGORY_NOT_FOUND') {
        errorMessage = __(
          'Zvolená kategória už neexistuje. Vyberte inú alebo podnet odošlite bez kategórie.',
        );
        toastError(errorMessage);
      } else if (e.details?.name === 'INVALID_FORMAT') {
        errorMessage = __(
          'Nahrávaný súbor má nepodporovaný formát alebo je poškodený.',
        );
        toastError(errorMessage);
      } else if (e.details?.name === 'MEDIA_TYPE_MISMATCH') {
        errorMessage = __(
          'Neočkávaný formát. Skontroluje súbor a jeho príponu a skúste znova.',
        );
        toastError(errorMessage);
      } else if (e.details?.name === 'OFFLINE') {
        errorMessage = __(
          'Stratili ste internetové pripojenie. Skontrolujte prosím svoje pripojenie a skúste to znova.',
        );
        toastError(errorMessage);
      } else if (
        authState === 'unauthorized' &&
        e.details?.statusCode === 403
      ) {
        // Pre neprihlásených používateľov ignorujeme 403 chybu z API (znamená, že reCAPTCHA zlyhala)
        errorMessage = __('Overenie zlyhalo. Skúste to znova.');
        toastError(__('Kontrola reCAPTCHA zlyhala.'));
        console.error('reCAPTCHA error:', e);
      } else {
        // Handle network errors using NetworkErrorHandler
        const errorInfo = getErrorDisplayInfo(e);
        errorMessage = errorInfo.message;
        toastError(errorInfo.message);
      }

      // Zobrazíme error modal s možnosťou zopakovať akciu
      setResultModalState({
        open: true,
        type: 'error',
        message: errorMessage,
        reportId: undefined,
      });
    },
  });

  React.useEffect(() => {
    setActions([
      {
        children: (
          <>
            <MissingLocationIndicator
              sx={{
                // Posun ikonu vľavo keď je reCAPTCHA viditeľná (pre neprihlásených používateľov)
                ...(authState === 'unauthorized' && {
                  position: 'relative',
                  right: 70, // Posun vľavo o 70px aby sa vyhla reCAPTCHA badge
                }),
              }}
            />
            <MissingInternetConnectionIndicator />
          </>
        ),
      },
    ]);

    setFooter(
      <Stack direction={'row'} justifyContent={'flex-end'}>
        <Button
          color='info'
          fullWidth={isNotSm}
          form='report-create-form'
          type='submit'
          disabled={authState === 'authorizing'}
          loading={createReportMutation.isPending}
          loadingIndicator={
            <Stack gap={1} alignItems={'center'} direction='row'>
              <Typography
                noWrap
                sx={{
                  fontSize: 14,
                }}
              >
                {__('Vytvára sa...')}
              </Typography>
              <CircularProgress size={16} />
            </Stack>
          }
          size={isMd ? 'medium' : 'small'}
        >
          {__('Vytvoriť podnet')}
        </Button>
      </Stack>,
    );

    return () => {
      setFooter(null);
    };
  }, [
    setActions,
    setFooter,
    isNotSm,
    isMd,
    createReportMutation.isPending,
    authState,
  ]);

  return (
    <Stack>
      <Card
        sx={{
          flex: 1,
          height: 'fit-content',
          borderWidth: {
            xs: 0,
            md: 1,
          },
        }}
      >
        <Stack gap={2}>
          <Form
            keepDirtyOnReinitialize
            initialValues={{
              media: [],
              title: '',
              description: '',
              location: null as GeoLocation | null,
              category_id: null as string | null,
              draftLocation: null as GeoLocation | null,
              showDraftedModal: false,
            }}
            onSubmit={async (values, form) => {
              if (!navigator.onLine) {
                toastError(__('Internetové pripojenie zlyhalo.'));
                return;
              }

              // Kontrola draft lokacie
              if (values.draftLocation) {
                form.change('showDraftedModal', true);
                return;
              }

              // Posledná poistka pred odoslaním. `FormMapField` mimo mesta
              // polohu nenastaví, takže sem by sa to nemalo dostať - ale
              // poloha vie prísť aj z adresy (plus kód) a odmietnutie zo
              // servera by prišlo až po nahratí všetkých fotiek.
              if (
                values.location &&
                !isInside(values.location[0], values.location[1])
              ) {
                toastError(
                  __('Podnet sa dá nahlásiť len na území mesta Prešov.'),
                );
                return;
              }

              const formData: ReportFormData = {
                title: values.title,
                description: values.description,
                location: values.location,
                media: values.media,
                // Pole vidí len úradník s oprávnením; inak sa neposiela vôbec,
                // nie ako prázdna hodnota.
                ...(canChooseCategory &&
                  values.category_id && { category_id: values.category_id }),
              };

              // Uložiť formData pre prípadné opakovanie
              setPendingFormData(formData);

              // Pre neprihlásených používateľov automaticky spustiť reCAPTCHA
              if (!authUser) {
                if (!executeRecaptcha) {
                  setResultModalState({
                    open: true,
                    type: 'error',
                    message: __('reCAPTCHA nie je dostupná. Skúste to znova.'),
                    reportId: undefined,
                  });
                  return;
                }

                // Automaticky spustiť reCAPTCHA overenie
                const token = await executeRecaptcha('create_report');
                if (token) {
                  // reCAPTCHA úspešná, pokračujeme s vytvorením podnetu
                  await createReportMutation.mutateAsync({
                    ...formData,
                    recaptchaToken: token,
                  });
                } else {
                  // reCAPTCHA zlyhala
                  setResultModalState({
                    open: true,
                    type: 'error',
                    message: __('Overenie zlyhalo. Skúste to znova.'),
                    reportId: undefined,
                  });
                }
              } else {
                // Pre prihlásených rovno vytvoriť podnet
                await createReportMutation.mutateAsync(formData);
              }
            }}
            render={({ handleSubmit, values, form }) => {
              // customHandleSubmit, kvoli scrollovaniu na prvy error field
              const customHandleSubmit = async (
                event: React.FormEvent<HTMLFormElement>,
              ) => {
                event.preventDefault();

                const result = await handleSubmit(event);

                const state = form.getState();
                const errors = state.errors;

                if (errors && Object.keys(errors).length > 0) {
                  // Poradie poli v formulari
                  const fieldOrder = [
                    'media',
                    'title',
                    'description',
                    'location',
                  ];

                  const firstErrorField = fieldOrder.find(
                    (field) => errors[field],
                  );

                  if (firstErrorField) {
                    let element: Element | null = null;

                    if (firstErrorField === 'media') {
                      element = document
                        .querySelector('input[name="media"]')
                        ?.closest('.MuiFormControl-root');
                    } else {
                      element = document.querySelector(
                        `[name="${firstErrorField}"]`,
                      );
                    }

                    if (element) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    }
                  }
                }

                return result;
              };

              // Calculate GPS usage state for each file
              const calculateGpsUsageState = (): Record<
                string,
                GpsUsageState
              > => {
                const state: Record<string, GpsUsageState> = {};
                const currentLocation = form.getState().values.location;

                if (
                  !form.getState().values.media ||
                  !Array.isArray(form.getState().values.media)
                ) {
                  return state;
                }

                form.getState().values.media.forEach((file) => {
                  if (file.location) {
                    const tolerance = 0.0001;
                    const fileLon = file.location.longitude;
                    const fileLat = file.location.latitude;

                    // Check if file location matches current map location
                    let isUsed = false;
                    if (currentLocation) {
                      const [mapLon, mapLat] = currentLocation;
                      isUsed =
                        Math.abs(fileLon - mapLon) < tolerance &&
                        Math.abs(fileLat - mapLat) < tolerance;
                    }

                    // Check if file location matches draft location
                    let isDraft = false;
                    if (form.getState().values.draftLocation) {
                      const [draftLon, draftLat] =
                        form.getState().values.draftLocation;
                      isDraft =
                        Math.abs(fileLon - draftLon) < tolerance &&
                        Math.abs(fileLat - draftLat) < tolerance;
                    }

                    if (isUsed) {
                      state[file.name] = 'used';
                    } else if (isDraft) {
                      state[file.name] = 'draft';
                    } else {
                      state[file.name] = 'unused';
                    }
                  }
                });

                return state;
              };

              const gpsUsageState = calculateGpsUsageState();

              return (
                <form onSubmit={customHandleSubmit} id='report-create-form'>
                  <Stack>
                    <FormMediaField
                      name='media'
                      accept={[
                        'image/jpeg',
                        'image/png',
                        'image/webp',
                        'image/tiff',
                        'video/mp4',
                        'image/heic',
                        'video/mov',
                        'video/quicktime',
                        // Písomný podklad (revízna správa, list, výkres). API
                        // ho uloží tak, ako prišiel, reports-analyzer ho pri
                        // kategorizácii aj duplicitách preskočí - číta len
                        // obrázky - a nginx médií ho podá inline.
                        'application/pdf',
                      ]}
                      maxFileSizeMB={config.CUSTOM_LIMIT.MEDIA_UPLOAD_MB}
                      allowCapture
                      sx={{ mb: 2 }}
                      required
                      validate={composeValidators(requiredArray, (values) =>
                        validateMaxFileSize(
                          values,
                          config.CUSTOM_LIMIT.MEDIA_UPLOAD_MB,
                        ),
                      )}
                      onMetadata={(metadata) => {
                        if (!metadata) return;

                        mapFieldRef.current?.navigateTo(
                          [metadata.longitude, metadata.latitude],
                          true,
                        );
                      }}
                      onFileChange={async (files) => {
                        const file = files.find(
                          (file) => 'location' in file && file.location,
                        );

                        if (file) {
                          const metadata = await getLocationMetadataFromMedia([
                            file,
                          ]);
                          if (metadata) {
                            mapFieldRef.current?.importMetadata([
                              metadata.longitude,
                              metadata.latitude,
                            ]);
                          }
                        }
                      }}
                      gpsUsageState={gpsUsageState}
                      onGpsClick={(fileName, gpsLocation) => {
                        const currentState = gpsUsageState[fileName];

                        // move to draft position
                        if (currentState !== 'used') {
                          // Set GPS location as draft location and navigate to it
                          form.change('draftLocation', gpsLocation);

                          // Navigate map to the GPS location
                          if (gpsLocation && mapFieldRef.current) {
                            mapFieldRef.current.navigateTo(gpsLocation, false);
                          }
                        }
                        // move back to used position
                        else {
                          form.change('draftLocation', undefined);

                          if (gpsLocation && mapFieldRef.current) {
                            mapFieldRef.current.navigateTo(gpsLocation, false);
                          }
                        }

                        mapWrapper.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                    />
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      flex={1}
                      gap={{ xs: 0, lg: 5 }}
                      sx={{ height: { lg: '400px' } }} // Nastavím fixnú výšku na desktop
                    >
                      <Stack
                        flex={1}
                        sx={{ display: 'flex', flexDirection: 'column' }}
                      >
                        {canChooseCategory && (
                          <FormAutocompleteField
                            name='category_id'
                            label={__('Kategória')}
                            options={categoryOptions}
                            loading={isLoadingCategories}
                            noOptionsText={__('Žiadna kategória')}
                            info={__(
                              'Nepovinné. Podnet so zvolenou kategóriou pôjde rovno jej správcom a automatické zaradenie sa preň nespustí. Bez kategórie ho zaradí aplikácia sama.',
                            )}
                          />
                        )}
                        <FormTextField
                          name='title'
                          label={__('Názov')}
                          placeholder={__('Zadajte názov podnetu')}
                          required
                          validate={required}
                          maxLength={250}
                        />
                        <FormTextField
                          name='description'
                          label={__('Krátky popis')}
                          multiline
                          rows={isLg ? undefined : 3}
                          placeholder={__('Zadajte popis podnetu')}
                          required
                          validate={required}
                          maxLength={2000}
                          sx={{
                            flex: isLg ? 1 : 'unset',
                            display: 'flex',
                            flexDirection: 'column',
                            '& .MuiTextField-root': {
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            },
                            '& .MuiInputBase-root': {
                              flex: 1,
                              alignItems: 'stretch',
                            },
                            '& .MuiInputBase-input': {
                              flex: 1,
                              resize: 'none',
                            },
                          }}
                        />
                      </Stack>
                      <Box flex={1} ref={mapWrapper}>
                        <FormMapField
                          name='location'
                          label='Poloha na mape'
                          ref={mapFieldRef}
                          defaultValue={async () => {
                            // Hľadáme lokáciu v nahratých súboroch z values.media
                            if (
                              values.media &&
                              Array.isArray(values.media) &&
                              values.media.length > 0
                            ) {
                              const metadata =
                                await getLocationMetadataFromMedia(
                                  values.media,
                                );

                              if (metadata) {
                                return [metadata.longitude, metadata.latitude];
                              }
                            }

                            return undefined;
                          }}
                          deleteLocationLabel={__(
                            'Podnety je možné odoslať aj bez udania polohy.',
                          )}
                        />
                      </Box>
                    </Stack>
                  </Stack>

                  <ConfirmationDialog
                    open={values.showDraftedModal}
                    title={__('Máte neuloženú polohu')}
                    description={__('Prajete si uložiť vybranú polohu?')}
                    confirmLabel={__('Uložiť polohu')}
                    closeLabel={__('Zrušiť polohu')}
                    fullWidth
                    maxWidth='xs'
                    onClose={() => {
                      if (
                        values.location &&
                        Array.isArray(values.location) &&
                        values.location.length >= 2
                      ) {
                        mapFieldRef.current?.navigateTo(
                          [values.location[0], values.location[1]],
                          false,
                        );
                      }

                      form.change('draftLocation', null);
                      form.change('showDraftedModal', false);
                    }}
                    onConfirm={async () => {
                      if (
                        values.draftLocation &&
                        Array.isArray(values.draftLocation) &&
                        values.draftLocation.length >= 2
                      ) {
                        mapFieldRef.current?.navigateTo(
                          [values.draftLocation[0], values.draftLocation[1]],
                          false,
                        );
                      }

                      if (values.draftLocation) {
                        form.change('location', values.draftLocation);
                        form.change('draftLocation', null);
                      }

                      form.change('showDraftedModal', false);
                    }}
                  />
                </form>
              );
            }}
          />
        </Stack>
      </Card>
      <ReportCreateResultModal
        open={resultModalState.open}
        type={resultModalState.type}
        message={resultModalState.message}
        reportId={resultModalState.reportId}
        shouldRedirectOnClose={resultModalState.type === 'success'}
        successText={
          resultModalState.type === 'success' && authUser
            ? __('Zobraziť detail podnetu')
            : __('Prejsť na domovskú stránku')
        }
        successHref={
          resultModalState.type === 'success' && authUser
            ? resultModalState.reportId
              ? `/podnety/${shortId(resultModalState.reportId)}`
              : profilePath(DEFAULT_PROFILE_SECTION)
            : '/'
        }
        onClose={() => {
          setResultModalState((prev) => ({
            ...prev,
            open: false,
            message: undefined,
            reportId: undefined,
          }));
          setPendingFormData(null);
          // Pre error len zavrie modal bez presmerovania,
          // redirect pri úspechu zabezpečuje Link v modale
        }}
        onRetry={async () => {
          if (!pendingFormData) return;

          setResultModalState((prev) => ({
            ...prev,
            open: false,
          }));

          // Pre neprihlásených používateľov znovu spustiť reCAPTCHA
          if (!authUser) {
            if (!executeRecaptcha) {
              setResultModalState({
                open: true,
                type: 'error',
                message: __('reCAPTCHA nie je dostupná. Skúste to znova.'),
                reportId: undefined,
              });
              return;
            }

            const token = await executeRecaptcha('create_report');
            if (token) {
              await createReportMutation.mutateAsync({
                ...pendingFormData,
                recaptchaToken: token,
              });
            } else {
              setResultModalState({
                open: true,
                type: 'error',
                message: __('Overenie zlyhalo. Skúste to znova.'),
                reportId: undefined,
              });
            }
          } else {
            // Pre prihlásených rovno vytvoriť podnet
            await createReportMutation.mutateAsync(pendingFormData);
          }
        }}
      />
    </Stack>
  );
};
export default ReportCreatePage;
